import { createArraySorter, type ReadonlyDate, type ReadonlyRecord } from '../utils'

import type { Loadable } from './Loadable'

export enum CertificateKeyUsage {
    CRITICAL = 'CRITICAL',
    CRL_SIGN = 'CRL_SIGN',
    DATA_ENCIPHERMENT = 'DATA_ENCIPHERMENT',
    DECIPHER_ONLY = 'DECIPHER_ONLY',
    DIGITAL_SIGNATURE = 'DIGITAL_SIGNATURE',
    ENCIPHER_ONLY = 'ENCIPHER_ONLY',
    KEY_AGREEMENT = 'KEY_AGREEMENT',
    KEY_CERTSIGN = 'KEY_CERTSIGN',
    KEY_ENCIPHERMENT = 'KEY_ENCIPHERMENT',
    NON_REPUDIATION = 'NON_REPUDIATION',
}

export enum CertificateExtendedKeyUsage {
    CLIENT_AUTH = 'CLIENT_AUTH',
    CODE_SIGNING = 'CODE_SIGNING',
    CRITICAL = 'CRITICAL',
    EMAIL_PROTECTION = 'EMAIL_PROTECTION',
    IPSEC_IKE = 'IPSEC_IKE',
    MS_CODE_COM = 'MS_CODE_COM',
    MS_CODE_IND = 'MS_CODE_IND',
    MS_CTL_SIGN = 'MS_CTL_SIGN',
    MS_EFS = 'MS_EFS',
    OCSP_SIGNING = 'OCSP_SIGNING',
    SERVER_AUTH = 'SERVER_AUTH',
    TIME_STAMPING = 'TIME_STAMPING',
}

export type Certificate = Readonly<{
    id: string
    issuedBy: Readonly<{ commonName: string | null, organization: string, organizationalUnit: string | null }>

    issuedTo: Readonly<{ commonName: string | null, organization: string | null, organizationalUnit: string | null, alternativeNames: readonly string[] }>

    keyUsage: readonly CertificateKeyUsage[]
    extendedKeyUsage: readonly CertificateExtendedKeyUsage[]
    basicConstraints: Readonly<{ authority: true, pathLength: number | null } | { authority: false }>

    issuedOn: ReadonlyDate
    expiresOn: ReadonlyDate

    fingerprints: ReadonlyRecord<'sha1' | 'sha256', string>

    removable: boolean
}>

const getCertificateIssuedByTitle = (issuedBy: Certificate['issuedBy']): string => issuedBy.commonName || issuedBy.organization
export const getCertificateTitle = (certificate: Pick<Certificate, 'issuedBy'>): string => getCertificateIssuedByTitle(certificate.issuedBy)

/**
 * @description Sort certificates by commonName or organization
 */
export const certificatesSorter = createArraySorter<Certificate>(
    {
        id: false,
        issuedBy: (a, b) => getCertificateIssuedByTitle(a).localeCompare(getCertificateIssuedByTitle(b)),
        issuedTo: false,
        keyUsage: false,
        extendedKeyUsage: false,
        basicConstraints: false,
        issuedOn: false,
        expiresOn: false,
        fingerprints: false,
        removable: false,
    },
    'certificatesSorter'
)

export type CertificatesAppState = Readonly<{ certificates: Loadable<readonly Certificate[]> }>

export const selectCertificates = (s: CertificatesAppState): CertificatesAppState['certificates'] => s.certificates
